import { money } from "@/lib/api";
import { LineItem, Pricing, PAYMENT_METHODS } from "@/lib/types";

export function PricingTable({ items, pricing, acceptance }: { items: LineItem[]; pricing: Pricing; dark?: boolean; acceptance?: any }) {
  const border = { borderBottom: "1px solid var(--page-border)" };
  const muted = { color: "var(--page-muted)" };
  const strong = { borderColor: "var(--page-text)" };
  return (
    <div className="font-proposal" data-testid="pricing-table">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b-2 text-[0.65em] uppercase tracking-[0.2em]" style={{ ...strong, ...muted }}>
            <th className="py-2 font-medium">Item</th><th className="py-2 text-right font-medium">Qty</th><th className="py-2 text-right font-medium">Rate</th><th className="py-2 text-right font-medium">Amount</th>
          </tr>
        </thead>
        <tbody>
          {items.map((i) => (
            <tr key={i.id} className="align-top text-[0.9em]" style={border} data-testid={`pricing-row-${i.id}`}>
              <td className="py-2.5 pr-3">
                <span>{i.name}</span>
                {i.is_addon && <span className="ml-2 rounded-full px-2 py-0.5 text-[0.65em] tracking-wide" style={{ background: "var(--accent)", color: "#fff" }}>Add-on</span>}
                {i.description && <div className="mt-0.5 text-[0.8em]" style={muted}>{i.description}</div>}
                {i.components && i.components.length > 0 && (
                  <div className="mt-1 flex flex-wrap items-center gap-1" data-testid={`pricing-components-${i.id}`}>
                    <span className="text-[0.6em] uppercase tracking-[0.15em]" style={muted}>Includes</span>
                    {i.components.map((c, k) => <span key={k} className="rounded-sm px-1.5 py-[0.1em] text-[0.62em]" style={{ border: "1px solid var(--page-border)", color: "var(--page-text)", opacity: 0.85 }}>{c}</span>)}
                  </div>
                )}
              </td>
              <td className="whitespace-nowrap py-2.5 pl-3 text-right">{i.qty} {i.unit}</td>
              <td className="whitespace-nowrap py-2.5 pl-3 text-right">{money(Number(i.unit_price))}</td>
              <td className="whitespace-nowrap py-2.5 pl-3 text-right">{money(Number(i.qty) * Number(i.unit_price))}</td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-[0.85em]" style={muted}>No line items yet</td></tr>}
        </tbody>
      </table>
      <div className="ml-auto mt-4 w-[55%] text-[0.9em]">
        <div className="flex justify-between py-1.5" style={border}><span>Subtotal</span><span data-testid="pricing-subtotal">{money(pricing.subtotal)}</span></div>
        {pricing.discounts.map((d) => (
          <div key={d.id} className="flex justify-between py-1.5 text-emerald-600" style={border}><span>{d.name}{d.type === "percent" ? ` (${d.value}%)` : ""}</span><span>-{money(d.amount)}</span></div>
        ))}
        {pricing.tax > 0 && <div className="flex justify-between py-1.5" style={border}><span>Tax ({pricing.tax_rate}%)</span><span>{money(pricing.tax)}</span></div>}
        <div className="mt-2 flex items-baseline justify-between border-t-2 pt-3" style={strong}>
          <span className="text-[1.1em] font-medium">Total</span>
          <span className="font-serif-display text-[1.9em]" style={{ fontFamily: "var(--heading-font)" }} data-testid="pricing-total">{money(pricing.total)}</span>
        </div>
        {pricing.deposit && pricing.deposit.amount > 0 && (
          <div className="mt-2 flex items-baseline justify-between rounded px-2 py-1.5" style={{ border: "1.5px solid var(--accent)", color: "var(--page-text)" }} data-testid="pricing-deposit">
            <span className="text-[0.8em] uppercase tracking-[0.15em]">Deposit to confirm{pricing.deposit.type === "percent" ? ` · ${pricing.deposit.value}%` : ""}</span>
            <span className="text-[1.05em] font-medium" style={{ color: "var(--accent)" }}>{money(pricing.deposit.amount)}</span>
          </div>
        )}
      </div>
      {pricing.deposit && pricing.deposit.note && <p className="mt-4 text-[0.75em] italic leading-relaxed" style={muted} data-testid="pricing-deposit-note">{pricing.deposit.note}</p>}
      <div className="mt-3 rounded px-3 py-2.5" style={{ border: "1px solid var(--page-border)" }} data-testid="pricing-payment-options">
        <div className="text-[0.55em] font-semibold uppercase tracking-[0.25em]" style={{ color: "var(--accent)" }}>Payment options</div>
        <ul className="mt-1.5 space-y-1">
          {PAYMENT_METHODS.map((m) => {
            const chosen = acceptance?.payment_method === m.value;
            return <li key={m.value} className={`relative pl-[1.1em] text-[0.72em] leading-snug ${chosen ? "font-medium" : "opacity-85"}`} data-testid={`payment-option-${m.value}`}>
              <span className="absolute left-0 top-[0.35em] h-[0.5em] w-[0.5em] rounded-full" style={{ border: "1.5px solid var(--accent)", background: chosen ? "var(--accent)" : "transparent" }} />{m.label}{chosen && <b className="ml-1.5" style={{ color: "var(--accent)" }}>· Selected</b>}
            </li>;
          })}
        </ul>
        <p className="mt-2 text-[0.68em]" style={muted}>Deposit due on booking · Final payment due on the date of install.</p>
      </div>
    </div>
  );
}
